import { MDBTable, MDBTableBody, MDBTableHead } from "mdb-react-ui-kit"
import React, { useEffect, useRef, useState } from "react"
import { Helmet } from "react-helmet"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import Header from "../components/Header"
import Loader from "../components/Loader"
import Paginate from "../components/Paginate"
import SideNav from "../components/SideNav"
import { formatDate } from "../main"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import moment from "moment"
import useHasPermission from "../hooks/useHasPermission"
import { PERMISSIONS } from "../constants/permissions"
import {
  fetchFailedPayoutTransactions,
  fetchFailedBillTransactions,
  reverseFailedTransaction,
  markTransactionSuccessful,
} from "../actions/transactionActions"

const FailedTransactionsScreen = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const didFetch = useRef(true)

  const [activeTab, setActiveTab] = useState("payouts")
  const [currPage, setCurrPage] = useState(1)
  const year = new Date().getFullYear()
  const [startDate, setStartDate] = useState(new Date(year, 0, 1))
  const [endDate, setEndDate] = useState(new Date())
  const [email, setEmail] = useState("")
  const [transactionReference, setTransactionReference] = useState("")
  const [filterOpen, setFilterOpen] = useState(false)

  const admin2faLogin = useSelector((state) => state.admin2faLogin)
  const { userInfo } = admin2faLogin

  const canViewFailed = useHasPermission(PERMISSIONS.VIEW_FAILED_TRANSACTIONS)
  const canReverse = useHasPermission(PERMISSIONS.REVERSE_FAILED_TRANSACTION)
  const canMarkSuccessful = useHasPermission(PERMISSIONS.MARK_TRANSACTION_SUCCESSFUL)

  const failedPayouts = useSelector((state) => state.failedPayoutTransactions)
  const { loading: payoutsLoading, success: payoutsSuccess, error: payoutsError, transactions: payoutsData } = failedPayouts

  const failedBills = useSelector((state) => state.failedBillTransactions)
  const { loading: billsLoading, success: billsSuccess, error: billsError, transactions: billsData } = failedBills

  const reverseState = useSelector((state) => state.reverseTransaction)
  const { loading: reverseLoading, success: reverseSuccess, error: reverseError } = reverseState

  const markState = useSelector((state) => state.markTransactionSuccessful)
  const { loading: markLoading, success: markSuccess, error: markError } = markState

  useEffect(() => {
    if (!userInfo || userInfo === null) {
      navigate("/", { replace: true })
    }
    if (userInfo?.user?.role === "Account Officer") {
      navigate("/performance", { replace: true })
    }
    if (reverseSuccess || markSuccess) {
      dispatch({ type: "REVERSE_TRANSACTION_RESET" })
      dispatch({ type: "MARK_TRANSACTION_SUCCESSFUL_RESET" })
      window.location.reload()
    }
    if (didFetch.current) {
      dispatch(fetchFailedPayoutTransactions(currPage))
      dispatch(fetchFailedBillTransactions(currPage))
      didFetch.current = false
    }
  }, [dispatch, navigate, userInfo, reverseSuccess, markSuccess, currPage])

  const getFilterData = () => ({
    email,
    transactionReference,
  })

  const handleFilter = () => {
    setFilterOpen(false)
    const filterData = getFilterData()
    if (activeTab === "payouts") {
      dispatch(fetchFailedPayoutTransactions(currPage, filterData))
    } else {
      dispatch(fetchFailedBillTransactions(currPage, filterData))
    }
  }

  const handleClear = () => {
    setEmail("")
    setTransactionReference("")
    setFilterOpen(false)
    if (activeTab === "payouts") {
      dispatch(fetchFailedPayoutTransactions(currPage))
    } else {
      dispatch(fetchFailedBillTransactions(currPage))
    }
  }

  const afterPageClicked = (page_number) => {
    const next = page_number === currPage ? page_number + 1 : page_number
    setCurrPage(next)
    const filterData = getFilterData()
    if (activeTab === "payouts") {
      dispatch(fetchFailedPayoutTransactions(next, filterData))
    } else {
      dispatch(fetchFailedBillTransactions(next, filterData))
    }
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    setCurrPage(1)
    setEmail("")
    setTransactionReference("")
  }

  const handleReverse = (transactionId) => {
    dispatch(reverseFailedTransaction({ transaction_id: transactionId }))
  }

  const handleMarkSuccessful = (transactionId) => {
    dispatch(markTransactionSuccessful({ transaction_id: transactionId }))
  }

  const isPayouts = activeTab === "payouts"
  const loading = isPayouts ? payoutsLoading : billsLoading
  const error = isPayouts ? payoutsError : billsError
  const txData = isPayouts ? payoutsData : billsData
  const actionLoading = reverseLoading || markLoading

  return (
    <>
      <SideNav />
      <Helmet>
        <title>Failed Transactions | PennyTree - Admin</title>
      </Helmet>
      <section className="page-container">
        <div className="page-content">
          <Header />
          <div className="page-body">
            <h1>Failed Transactions</h1>

            {(reverseError || markError) && (
              <p style={{ color: "red", fontSize: "14px", letterSpacing: "1px" }}>
                {reverseError || markError}
              </p>
            )}

            {!canViewFailed ? (
              <p style={{ color: "red" }}>You do not have permission to view failed transactions.</p>
            ) : (
              <>
                <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
                  <button
                    className={`button${isPayouts ? "" : ""}`}
                    style={{
                      background: isPayouts ? "#4caf50" : "#1a1a1a",
                      border: "none",
                      padding: "10px 20px",
                      borderRadius: "8px",
                      color: "#fff",
                      cursor: "pointer",
                      fontWeight: isPayouts ? "bold" : "normal",
                    }}
                    onClick={() => handleTabChange("payouts")}
                  >
                    Failed Payouts
                  </button>
                  <button
                    style={{
                      background: !isPayouts ? "#4caf50" : "#1a1a1a",
                      border: "none",
                      padding: "10px 20px",
                      borderRadius: "8px",
                      color: "#fff",
                      cursor: "pointer",
                      fontWeight: !isPayouts ? "bold" : "normal",
                    }}
                    onClick={() => handleTabChange("bills")}
                  >
                    Failed Bills
                  </button>
                </div>

                <div className="filter-box">
                  <div className="filter">
                    <div className="filter-container">
                      <p style={{ cursor: "pointer" }} onClick={() => setFilterOpen(!filterOpen)}>
                        Filter
                      </p>
                      <div className="navContainer" style={{ display: filterOpen ? "block" : "none" }}>
                        <div className="date-filter">
                          <div className="date-title">Date</div>
                          <div className="date-options">
                            <div className="date-content">
                              <div className="date-items">
                                <DatePicker selected={startDate} onChange={(d) => setStartDate(d)} />
                                <DatePicker selected={endDate} onChange={(d) => setEndDate(d)} />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="status-container">
                          <div className="date-title">Other Filters</div>
                          <div className="checkbox checkbox--flex">
                            <div className="checkbox__item" style={{ width: "100%" }}>
                              <input
                                style={{
                                  width: "100%",
                                  borderRadius: "10px",
                                  border: "1px solid grey",
                                  fontSize: "14px",
                                  display: transactionReference ? "none" : "block",
                                }}
                                type="email"
                                className="email-input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter customer email"
                              />
                            </div>
                            <div className="checkbox__item" style={{ width: "100%" }}>
                              <input
                                style={{
                                  width: "100%",
                                  borderRadius: "10px",
                                  border: "1px solid grey",
                                  fontSize: "14px",
                                  display: email ? "none" : "block",
                                }}
                                type="text"
                                className="email-input"
                                value={transactionReference}
                                onChange={(e) => setTransactionReference(e.target.value)}
                                placeholder="Enter transaction reference"
                              />
                            </div>
                          </div>
                        </div>
                        <div
                          className="status-container"
                          style={{ display: "flex", justifyContent: "space-evenly", alignItems: "center" }}
                        >
                          <button className="button" onClick={handleClear}><p>Clear</p></button>
                          <button className="button" onClick={handleFilter}><p>Filter</p></button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <Loader />
                ) : error ? (
                  <p style={{ color: "red" }}>{error}</p>
                ) : (
                  <>
                    <MDBTable responsive style={{ width: "100%", fontSize: "14px" }}>
                      <MDBTableHead>
                        <tr className="font-reduce">
                          <th scope="col">S/N</th>
                          <th scope="col">Customer Name</th>
                          <th scope="col">Customer Email</th>
                          <th scope="col">Amount</th>
                          <th scope="col">Transaction Reference</th>
                          <th scope="col">Product</th>
                          <th scope="col">Status</th>
                          <th scope="col">Remarks</th>
                          <th scope="col">Date</th>
                          {(canReverse || canMarkSuccessful) && <th scope="col">Actions</th>}
                        </tr>
                      </MDBTableHead>
                      <MDBTableBody>
                        {txData?.items?.map((item, key) => (
                          <tr key={key} className="font-reduce">
                            <td>{key + 1}</td>
                            <td>{item?.customer_full_name}</td>
                            <td>{item?.customer_email}</td>
                            <td>{item?.amount ?? item?.transaction_amount}</td>
                            <td>{item?.transaction_reference}</td>
                            <td>{item?.product}</td>
                            <td>{item?.status}</td>
                            <td>{item?.remarks}</td>
                            <td>{item?.date_created ? moment(item.date_created).format("LLL") : "-"}</td>
                            {(canReverse || canMarkSuccessful) && (
                              <td>
                                {actionLoading ? (
                                  <Loader />
                                ) : (
                                  <div style={{ display: "flex", gap: "6px" }}>
                                    {canReverse && (
                                      <button
                                        className="withdrawal-btn"
                                        onClick={() => handleReverse(item?.id)}
                                      >
                                        Reverse
                                      </button>
                                    )}
                                    {canMarkSuccessful && (
                                      <button
                                        className="withdrawal-btn"
                                        style={{ background: "#2196f3" }}
                                        onClick={() => handleMarkSuccessful(item?.id)}
                                      >
                                        Mark Successful
                                      </button>
                                    )}
                                  </div>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </MDBTableBody>
                    </MDBTable>
                    <Paginate
                      totPages={txData?.page?.total_page}
                      currentPage={String(txData?.page?.currentPage)}
                      page={String(txData?.page?.currentPage)}
                      pageClicked={(ele) => afterPageClicked(ele)}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </section>
    </>
  )
}

export default FailedTransactionsScreen
